import { useState, useCallback, useEffect } from 'react';
import { OKRState, Task, Objective, KPI, ActionPlan } from './types';
import { MOCK_DATA } from './mockData';
import { normaliseId, generateRecurringTasks } from './helpers';
import { apiUserToMember, refreshTeamStats } from './teamHelpers';
import {
    getOKRDashboard,
    createObjective as apiCreateObj,
    updateObjective as apiUpdateObjective,
    deleteObjective as apiDeleteObjective,
    createKPI as apiCreateKPI,
    updateKPI as apiUpdateKPI,
    deleteKPI as apiDeleteKPI,
    createActionPlan as apiCreatePlan,
    createOKRTask as apiCreateTask,
    updateOKRTask as apiUpdateTask,
    deleteOKRTask as apiDeleteTask,
    updateOKRTaskStatus as apiPatchStatus,
    acknowledgeOKRAlert as apiAckAlert,
} from 'interfaces/okr';
import { getUsersLight } from 'interfaces/user';

const STORAGE_KEY = 'toola_okr_v2';

// ── Local persistence (offline / demo mode only) ──────────────────────────────

const loadLocal = (): OKRState => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw) as OKRState;
    } catch { /* ignore */ }
    return MOCK_DATA;
};

const MAX_LOCAL_BYTES = 4 * 1024 * 1024; // 4 MB guard (localStorage limit ~5-10 MB)

const saveLocal = (s: OKRState) => {
    try {
        const json = JSON.stringify(s);
        if (json.length > MAX_LOCAL_BYTES) {
            // State too large — persist a lightweight version without full history arrays
            const slim = { ...s, kpis: s.kpis.map((k) => ({ ...k, history: k.history.slice(-6) })) };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
        } else {
            localStorage.setItem(STORAGE_KEY, json);
        }
    } catch { /* quota exceeded — silently skip */ }
};

// ── Map API dashboard response → OKRState ────────────────────────────────────

function apiToState(data: any, prev: OKRState): OKRState {
    const norm = (o: any) => normaliseId(o);

    const objectives: Objective[] = (data.objectives ?? []).map((o: any) => {
        const n = norm(o);
        return {
            ...n,
            responsibleUserId: n.responsibleUserId ?? n.ownerId ?? '',
            ownerId: n.ownerId ?? n.responsibleUserId ?? '',
            kpiIds: [],
            actionPlanIds: [],
        };
    });

    const kpis: KPI[] = (data.kpis ?? []).map((k: any) => norm(k));

    objectives.forEach((obj) => {
        obj.kpiIds = kpis
            .filter((k) => String(k.objectiveId) === obj.id || String(k.objectiveId) === (obj._id ?? ''))
            .map((k) => k.id);
    });

    const tasks: Task[] = (data.tasks ?? []).map((t: any) => ({
        ...norm(t),
        actionPlanId: t.actionPlanId ?? '',
    }));

    const alerts = (data.alerts ?? []).map((a: any) => norm(a));

    // Keep whatever team we already have (refreshed separately)
    return { objectives, kpis, actionPlans: prev.actionPlans, tasks, team: prev.team, alerts };
}

// ── Store hook ────────────────────────────────────────────────────────────────

export const useOKRStore = () => {
    const [state, setState] = useState<OKRState>(loadLocal);
    const [loading, setLoading] = useState(false);
    const [apiAvailable, setApiAvailable] = useState(false);

    // Load OKR data + real users on mount
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setLoading(true);

            // Run both in parallel: dashboard data + company users
            const [dashData, usersRaw] = await Promise.all([
                getOKRDashboard(),
                getUsersLight(),
            ]);

            if (cancelled) return;

            if (dashData) {
                setState((prev) => {
                    const next = apiToState(dashData, prev);
                    // Build team from real API users, computing stats from live tasks
                    if (Array.isArray(usersRaw) && usersRaw.length > 0) {
                        next.team = usersRaw.map((u: any) => apiUserToMember(u, next.tasks));
                    }
                    return next;
                });
                setApiAvailable(true);
            } else if (Array.isArray(usersRaw) && usersRaw.length > 0) {
                // API OKR unavailable but users loaded → enrich local/demo state
                setState((prev) => ({
                    ...prev,
                    team: usersRaw.map((u: any) => apiUserToMember(u, prev.tasks)),
                }));
            }

            setLoading(false);
        };

        init();
        return () => { cancelled = true; };
    }, []);

    // ── Mutations ─────────────────────────────────────────────────────────────

    const mutate = useCallback((fn: (prev: OKRState) => OKRState) => {
        setState((prev) => {
            const next = fn(prev);
            if (!apiAvailable) saveLocal(next);
            return next;
        });
    }, [apiAvailable]);

    const refreshFromAPI = useCallback(async () => {
        const data = await getOKRDashboard();
        if (data) {
            setState((prev) => {
                const next = apiToState(data, prev);
                // Refresh team stats with updated tasks
                next.team = refreshTeamStats(prev.team, next.tasks);
                return next;
            });
        }
    }, []);

    const addObjective = useCallback(async (obj: Objective, kpis: KPI[] = []) => {
        if (apiAvailable) {
            const created = await apiCreateObj({
                name: obj.name,
                description: obj.description,
                category: obj.category,
                responsibleUserId: obj.responsibleUserId || obj.ownerId,
                startDate: obj.startDate,
                endDate: obj.endDate,
                priority: obj.priority,
                status: obj.status,
            });
            if (created) {
                for (const kpi of kpis) {
                    await apiCreateKPI({
                        objectiveId: created._id,
                        name: kpi.name,
                        currentValue: kpi.currentValue,
                        targetValue: kpi.targetValue,
                        minAcceptableValue: kpi.minAcceptableValue,
                        unit: kpi.unit,
                        direction: kpi.direction,
                        auto_sync: kpi.auto_sync ?? false,
                        module: kpi.module ?? '',
                        metric_key: kpi.metric_key ?? '',
                    });
                }
                await refreshFromAPI();
                return;
            }
        }
        mutate((prev) => ({
            ...prev,
            objectives: [...prev.objectives, obj],
            kpis: [...prev.kpis, ...kpis],
        }));
    }, [apiAvailable, mutate, refreshFromAPI]);

    const addTask = useCallback(async (task: Task) => {
        if (apiAvailable) {
            const created = await apiCreateTask({
                objectiveId: task.objectiveId || null,
                actionPlanId: task.actionPlanId || null,
                title: task.title,
                description: task.description,
                assigneeId: task.assigneeId,
                dueDate: task.dueDate,
                priority: task.priority,
                estimatedHours: task.estimatedHours,
                impactKPI: task.impactKPI,
                urgency: task.urgency,
                effort: task.effort,
                tags: task.tags ?? [],
            });
            if (created) {
                await refreshFromAPI();
                return;
            }
        }
        mutate((prev) => ({
            ...prev,
            tasks: [...prev.tasks, task],
            team: refreshTeamStats(prev.team, [...prev.tasks, task]),
        }));
    }, [apiAvailable, mutate, refreshFromAPI]);

    const addKPI = useCallback((kpi: KPI) => {
        mutate((prev) => ({ ...prev, kpis: [...prev.kpis, kpi] }));
    }, [mutate]);

    // ── Add action plan (+ auto-generate recurring tasks) ─────────────────────

    const addActionPlan = useCallback(async (plan: ActionPlan) => {
        if (apiAvailable) {
            const result = await apiCreatePlan({
                objectiveId: plan.objectiveId,
                name: plan.name,
                description: plan.description,
                responsibleUserId: plan.responsibleUserId,
                budget: plan.budget,
                deadline: plan.deadline,
                estimatedKPIImpact: plan.estimatedKPIImpact,
                status: plan.status,
                recurrence: plan.recurrence ?? 'none',
                recurrenceTaskTitle: plan.recurrenceTaskTitle ?? '',
            });
            if (result) {
                await refreshFromAPI();
                return;
            }
        }
        // Offline / demo mode: add plan + generate recurring tasks locally
        const recurringTasks = generateRecurringTasks(plan);
        mutate((prev) => ({
            ...prev,
            actionPlans: [...prev.actionPlans, plan],
            tasks: [...prev.tasks, ...recurringTasks],
            team: refreshTeamStats(prev.team, [...prev.tasks, ...recurringTasks]),
        }));
    }, [apiAvailable, mutate, refreshFromAPI]);

    const updateTaskStatus = useCallback(async (taskId: string, status: Task['status']) => {
        // Optimistic update: instant UI + recompute team stats
        mutate((prev) => {
            const tasks = prev.tasks.map((t) =>
                t.id === taskId
                    ? { ...t, status, completedAt: status === 'done' ? new Date().toISOString() : t.completedAt }
                    : t,
            );
            return {
                ...prev,
                tasks,
                team: refreshTeamStats(prev.team, tasks),
            };
        });
        if (apiAvailable) {
            apiPatchStatus(taskId, status).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    const acknowledgeAlert = useCallback(async (alertId: string) => {
        mutate((prev) => ({
            ...prev,
            alerts: prev.alerts.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
        }));
        if (apiAvailable) {
            apiAckAlert(alertId).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Update objective ──────────────────────────────────────────────────────

    const editObjective = useCallback(async (id: string, data: Record<string, any>) => {
        // Optimistic update
        mutate((prev) => ({
            ...prev,
            objectives: prev.objectives.map((o) =>
                o.id === id || o._id === id ? { ...o, ...data } : o,
            ),
        }));
        if (apiAvailable) {
            apiUpdateObjective(id, data).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Delete objective ──────────────────────────────────────────────────────

    const removeObjective = useCallback(async (id: string) => {
        mutate((prev) => ({
            ...prev,
            objectives: prev.objectives.filter((o) => o.id !== id && o._id !== id),
            kpis: prev.kpis.filter((k) => k.objectiveId !== id),
            actionPlans: prev.actionPlans.filter((p) => p.objectiveId !== id),
            tasks: prev.tasks.filter((t) => t.objectiveId !== id),
        }));
        if (apiAvailable) {
            apiDeleteObjective(id).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Update KPI ────────────────────────────────────────────────────────────

    const editKPI = useCallback(async (id: string, data: Record<string, any>) => {
        mutate((prev) => ({
            ...prev,
            kpis: prev.kpis.map((k) =>
                k.id === id || k._id === id ? { ...k, ...data } : k,
            ),
        }));
        if (apiAvailable) {
            apiUpdateKPI(id, data).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Delete KPI ────────────────────────────────────────────────────────────

    const removeKPI = useCallback(async (id: string) => {
        mutate((prev) => ({
            ...prev,
            kpis: prev.kpis.filter((k) => k.id !== id && k._id !== id),
        }));
        if (apiAvailable) {
            apiDeleteKPI(id).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Update task ───────────────────────────────────────────────────────────

    const editTask = useCallback(async (id: string, data: Record<string, any>) => {
        mutate((prev) => {
            const tasks = prev.tasks.map((t) =>
                t.id === id || t._id === id ? { ...t, ...data } : t,
            );
            return { ...prev, tasks, team: refreshTeamStats(prev.team, tasks) };
        });
        if (apiAvailable) {
            apiUpdateTask(id, data).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Delete task ───────────────────────────────────────────────────────────

    const removeTask = useCallback(async (id: string) => {
        mutate((prev) => {
            const tasks = prev.tasks.filter((t) => t.id !== id && t._id !== id);
            return { ...prev, tasks, team: refreshTeamStats(prev.team, tasks) };
        });
        if (apiAvailable) {
            apiDeleteTask(id).catch((): null => null);
        }
    }, [apiAvailable, mutate]);

    // ── Reset ─────────────────────────────────────────────────────────────────

    const resetToDemo = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setState(MOCK_DATA);
        setApiAvailable(false);
    }, []);

    return {
        state,
        loading,
        apiAvailable,
        addObjective,
        addTask,
        addKPI,
        addActionPlan,
        editObjective,
        removeObjective,
        editKPI,
        removeKPI,
        editTask,
        removeTask,
        updateTaskStatus,
        acknowledgeAlert,
        resetToDemo,
    };
};
